import {useCallback, useMemo} from 'react'
import {type GestureResponderEvent, View} from 'react-native'
import {type AppBskyFeedDefs} from '@atproto/api'
import {msg} from '@lingui/macro'
import {useLingui} from '@lingui/react'

import {logger} from '#/logger'
import {
  useAddSavedFeedsMutation,
  usePreferencesQuery,
  useRemoveFeedMutation,
  useUpdateSavedFeedsMutation,
} from '#/state/queries/preferences'
import {useSession} from '#/state/session'
import * as Toast from '#/view/com/util/Toast'
import {atoms as a} from '#/alf'
import {
  Button,
  ButtonIcon,
  type ButtonProps,
  ButtonText,
} from '#/components/Button'
import * as Menu from '#/components/Menu'
import {Check_Stroke2_Corner0_Rounded as Check} from '#/components/icons/Check'
import {Menu_Stroke2_Corner0_Rounded as MenuIcon} from '#/components/icons/Menu'
import {Pin_Stroke2_Corner0_Rounded as Pin} from '#/components/icons/Pin'
import {PlusLarge_Stroke2_Corner0_Rounded as Plus} from '#/components/icons/Plus'
import {Trash_Stroke2_Corner0_Rounded as Trash} from '#/components/icons/Trash'
import {TimesLarge_Stroke2_Corner0_Rounded as X} from '#/components/icons/Times'

function useFeedActions(feed: AppBskyFeedDefs.GeneratorView) {
  const {_} = useLingui()
  const {data: preferences} = usePreferencesQuery()
  const {mutateAsync: addSavedFeeds, isPending: isAddPending} =
    useAddSavedFeedsMutation()
  const {mutateAsync: removeFeed, isPending: isRemovePending} =
    useRemoveFeedMutation()
  const {mutateAsync: updateSavedFeeds, isPending: isUpdatePending} =
    useUpdateSavedFeedsMutation()

  const savedFeedConfig = useMemo(() => {
    return preferences?.savedFeeds?.find(f => f.value === feed.uri)
  }, [preferences?.savedFeeds, feed.uri])

  const isSaved = Boolean(savedFeedConfig)
  const isPinned = Boolean(savedFeedConfig?.pinned)
  const isPending = isAddPending || isRemovePending || isUpdatePending

  const toggleSaved = useCallback(
    async (e?: GestureResponderEvent) => {
      if (e) {
        e.preventDefault()
        e.stopPropagation()
      }

      try {
        if (savedFeedConfig) {
          await removeFeed(savedFeedConfig)
          Toast.show(_(msg`Removed from your feeds`))
          logger.metric('feed:unsave', {feedUrl: feed.uri})
        } else {
          await addSavedFeeds([
            {
              type: 'feed',
              value: feed.uri,
              pinned: false,
            },
          ])
          Toast.show(_(msg`Saved to your feeds`))
          logger.metric('feed:save', {feedUrl: feed.uri})
        }
      } catch (err) {
        Toast.show(
          _(
            msg`There was an issue updating your feeds, please check your internet connection and try again.`,
          ),
          'xmark',
        )
        logger.error('Failed to update feeds', {message: err})
      }
    },
    [_, addSavedFeeds, feed.uri, removeFeed, savedFeedConfig],
  )

  const togglePinned = useCallback(
    async (e?: GestureResponderEvent) => {
      if (e) {
        e.preventDefault()
        e.stopPropagation()
      }

      try {
        if (savedFeedConfig) {
          const pinned = !savedFeedConfig.pinned
          await updateSavedFeeds([
            {
              ...savedFeedConfig,
              pinned,
            },
          ])

          if (pinned) {
            Toast.show(_(msg`Pinned ${feed.displayName} to Home`))
            logger.metric('feed:pin', {feedUrl: feed.uri})
          } else {
            Toast.show(_(msg`Unpinned ${feed.displayName} from Home`))
            logger.metric('feed:unpin', {feedUrl: feed.uri})
          }
        } else {
          await addSavedFeeds([
            {
              type: 'feed',
              value: feed.uri,
              pinned: true,
            },
          ])
          Toast.show(_(msg`Pinned ${feed.displayName} to Home`))
          logger.metric('feed:pin', {feedUrl: feed.uri})
        }
      } catch (err) {
        Toast.show(_(msg`There was an issue contacting the server`), 'xmark')
        logger.error('Failed to toggle pinned feed', {message: err})
      }
    },
    [_, addSavedFeeds, feed.displayName, feed.uri, savedFeedConfig, updateSavedFeeds],
  )

  return {
    isSaved,
    isPinned,
    isPending,
    toggleSaved,
    togglePinned,
  }
}

export function FeedSaveButton({
  feed,
  size = 'small',
  style,
  variant,
  shape,
}: {
  feed: AppBskyFeedDefs.GeneratorView
  size?: ButtonProps['size']
  style?: ButtonProps['style']
  variant?: ButtonProps['variant']
  shape?: ButtonProps['shape']
}) {
  const {_} = useLingui()
  const {hasSession} = useSession()
  const {isSaved, isPending, toggleSaved} = useFeedActions(feed)

  if (!hasSession) return null

  const label = isSaved
    ? _(msg`Following`)
    : _(msg({message: 'Follow', comment: 'Follow a feed'}))

  return (
    <Button
      label={label}
      size={size}
      variant={variant || (isSaved ? 'outline' : 'solid')}
      color="secondary"
      shape={shape}
      onPress={toggleSaved}
      disabled={isPending}
      style={style}>
      <ButtonIcon icon={isSaved ? Check : Plus} size="sm" />
      <ButtonText>{label}</ButtonText>
    </Button>
  )
}

export function FeedActionsMenu({
  feed,
  label,
}: {
  feed: AppBskyFeedDefs.GeneratorView
  label?: string
}) {
  const {_} = useLingui()
  const {hasSession} = useSession()
  const {isSaved, isPinned, isPending, toggleSaved, togglePinned} =
    useFeedActions(feed)

  if (!hasSession) return null

  return (
    <Menu.Root>
      <Menu.Trigger label={label || _(msg`Feed actions`)}>
        {({props, state}) => (
          <Button
            {...props}
            label={label || _(msg`Feed actions`)}
            size="tiny"
            shape="round"
            variant="ghost"
            color="secondary"
            onPress={e => {
              e.preventDefault()
              e.stopPropagation()
              props.onPress?.()
            }}
            style={[state.pressed && {opacity: 0.7}]}>
            <ButtonIcon icon={MenuIcon} size="sm" />
          </Button>
        )}
      </Menu.Trigger>
      <Menu.Outer>
        <View style={[a.gap_xs]}>
          <Menu.Item
            disabled={isPending}
            label={
              isSaved
                ? _(msg`Remove from my feeds`)
                : _(msg`Save to my feeds`)
            }
            onPress={toggleSaved}>
            <Menu.ItemText>
              {isSaved
                ? _(msg`Remove from my feeds`)
                : _(msg`Save to my feeds`)}
            </Menu.ItemText>
            <Menu.ItemIcon icon={isSaved ? Trash : Plus} position="right" />
          </Menu.Item>
          <Menu.Item
            disabled={isPending}
            label={
              isPinned
                ? _(msg`Unpin from home`)
                : _(msg`Pin to home`)
            }
            onPress={togglePinned}>
            <Menu.ItemText>
              {isPinned ? _(msg`Unpin from home`) : _(msg`Pin to home`)}
            </Menu.ItemText>
            <Menu.ItemIcon icon={isPinned ? X : Pin} position="right" />
          </Menu.Item>
        </View>
      </Menu.Outer>
    </Menu.Root>
  )
}
